import { CHAT_MAX, formatParkCode, QUICK_CHAT } from '../net/protocol';
import type { SessionStatus } from '../net/Session';
import { clear, h } from './dom';

export interface OnlinePerson {
  name: string;
  portrait: string | undefined;
  detail: string;
}

export interface OnlineView {
  status: SessionStatus;
  code: string | null;
  /** The park we were in last time (offered as "Rejoin"). */
  lastCode: string | null;
  invite: string | null;
  /** How long we've been in the park, in ms. */
  waitingMs: number;
  me: OnlinePerson;
  neighbors: Array<OnlinePerson & { id: string; where: string }>;
  chat: ReadonlyArray<{ name: string; text: string; self: boolean }>;
  /** Park records: the best anybody here has done, and who did it. */
  records: ReadonlyArray<{ label: string; holder: string; value: string }>;
  /** The buyback jar, pooled across the park. */
  fund: { total: number; goal: number };
}

/** What the Online tab needs from the game (implemented by game/Neighbors). */
export interface OnlineHost {
  view(): OnlineView;
  onChange(fn: () => void): () => void;
  host(): void;
  /** False if the code isn't a valid park code. */
  join(code: string): boolean;
  leave(): void;
  /** False if nothing was sent (empty, or sending too fast). */
  say(text: string): boolean;
  copyInvite(): Promise<boolean>;
  readonly canShare: boolean;
  shareInvite(): void;
}

/** If nobody shows up after this long, offer some troubleshooting. */
const LONELY_MS = 45_000;

/** The phone's Online tab: open or join a park, see who's around, and chat. */
export class OnlinePanel {
  private root: HTMLDivElement | null = null;
  private shownStatus: SessionStatus | null = null;
  private statusLine: HTMLElement | null = null;
  private roster: HTMLElement | null = null;
  private records: HTMLElement | null = null;
  private log: HTMLElement | null = null;
  private chatInput: HTMLInputElement | null = null;
  private lastChatLength = -1;

  constructor(private readonly host: OnlineHost) {
    host.onChange(() => this.update());
  }

  /** Renders into the phone (which clears its body whenever the tab changes). */
  mount(container: HTMLElement): void {
    this.root = h('div', { class: 'online' });
    container.append(this.root);
    this.render();
  }

  focusChat(): void {
    this.chatInput?.focus({ preventScroll: true });
  }

  /** Refreshes the live parts, leaving anything being typed alone. */
  update(): void {
    if (!this.root?.isConnected) return;
    const v = this.host.view();
    if (v.status !== this.shownStatus) this.render();
    else this.fill(v);
  }

  private render(): void {
    const root = this.root;
    if (!root) return;
    clear(root);
    this.statusLine = this.roster = this.records = this.log = this.chatInput = null;
    this.lastChatLength = -1;
    const v = this.host.view();
    this.shownStatus = v.status;
    if (v.status === 'offline') this.renderOffline(root, v);
    else this.renderPark(root, v);
  }

  private renderOffline(root: HTMLElement, v: OnlineView): void {
    const error = h('div', { class: 'online-error', hidden: true });
    const input = h('input', {
      class: 'online-input',
      attrs: {
        type: 'text',
        maxlength: '7',
        placeholder: 'K7Q-X2M',
        autocomplete: 'off',
        autocapitalize: 'characters',
        spellcheck: 'false',
        'aria-label': 'Park code',
      },
    });
    const join = (): void => {
      error.hidden = this.host.join(input.value);
      error.textContent = "That ain't a park code. It's 6 letters and numbers, like K7Q-X2M.";
    };
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') join();
    });
    root.append(
      h('h3', { text: ':globe: Neighbors Online' }),
      h('p', {
        class: 'online-pitch',
        text: 'Invite friends into Lucky Acres. They walk around your park in their own game: same dirt road, separate wallets, and every neighbor gets a lot of their own on Possum Trot Lane.',
      }),
      h('button', { class: 'phone-btn', text: ':camp: Open my park', on: { click: () => this.host.host() } }),
      h('label', { text: 'Got a park code?' }),
      h(
        'div',
        { class: 'online-row' },
        input,
        h('button', { class: 'phone-btn', text: 'Join', on: { click: join } }),
      ),
      error,
    );
    const last = v.lastCode;
    if (last)
      root.append(
        h('button', {
          class: 'phone-btn secondary',
          text: `:back: Rejoin ${formatParkCode(last)}`,
          on: { click: () => this.host.join(last) },
        }),
      );
    root.append(this.privacyNote());
  }

  private renderPark(root: HTMLElement, v: OnlineView): void {
    this.statusLine = h('div', { class: 'park-status' });
    this.roster = h('div', { class: 'roster' });
    this.records = h('div', { class: 'records' });
    this.log = h('div', { class: 'chat-log', attrs: { role: 'log', 'aria-live': 'polite' } });
    const input = h('input', {
      class: 'online-input',
      attrs: {
        type: 'text',
        maxlength: String(CHAT_MAX),
        placeholder: "Say somethin'…",
        'aria-label': 'Chat',
      },
    });
    this.chatInput = input;
    const send = (): void => {
      if (this.host.say(input.value)) input.value = '';
    };
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') send();
    });
    const quick = h('div', { class: 'quick-chat' });
    for (const line of QUICK_CHAT)
      quick.append(h('button', { text: line, on: { click: () => this.host.say(line) } }));

    root.append(
      h(
        'div',
        { class: 'park-card' },
        h('div', { class: 'park-label', text: 'PARK CODE' }),
        h('div', { class: 'park-code', text: v.code ? formatParkCode(v.code) : '' }),
        this.statusLine,
        h(
          'div',
          { class: 'online-row' },
          h('button', {
            class: 'phone-btn',
            text: ':clipboard: Copy invite link',
            on: { click: () => void this.host.copyInvite() },
          }),
          this.host.canShare
            ? h('button', {
                class: 'phone-btn',
                text: ':share: Share',
                on: { click: () => this.host.shareInvite() },
              })
            : null,
        ),
      ),
      h('h4', { text: "Who's here" }),
      this.roster,
      h('h4', { text: ':trophy: Park Records' }),
      this.records,
      h('h4', { text: ':speech: Chat' }),
      this.log,
      quick,
      h(
        'div',
        { class: 'online-row' },
        input,
        h('button', { class: 'phone-btn', text: 'Send', on: { click: send } }),
      ),
      h('button', {
        class: 'phone-btn danger',
        text: ':door: Leave park',
        on: { click: () => this.host.leave() },
      }),
      this.privacyNote(),
    );
    this.fill(v);
  }

  private fill(v: OnlineView): void {
    if (!this.statusLine || !this.roster || !this.log) return;
    const n = v.neighbors.length;
    this.statusLine.textContent =
      v.status === 'joining'
        ? 'Knocking on doors…'
        : n > 0
          ? `${n} neighbor${n === 1 ? '' : 's'} in the park`
          : v.waitingMs > LONELY_MS
            ? 'Still nobody? Make sure they typed this exact code. Some school and work networks block games like this.'
            : "Nobody's here yet. Send 'em the link!";

    clear(this.roster);
    this.roster.append(this.person(v.me, 'You'));
    for (const p of v.neighbors) this.roster.append(this.person(p, p.where));

    if (this.records) {
      clear(this.records);
      const pct = Math.min(100, (v.fund.total / v.fund.goal) * 100);
      this.records.append(
        h('div', {
          class: 'record',
          text: `:jar: Buy back the park: $${Math.round(v.fund.total).toLocaleString()} / $${v.fund.goal.toLocaleString()} (chip in at the office)`,
        }),
        h('div', { class: 'progress' }, h('div', { style: { width: `${pct}%` } })),
      );
      for (const r of v.records)
        this.records.append(
          h('div', { class: 'record' }, `${r.label}: `, h('strong', { text: r.value }), ` · ${r.holder}`),
        );
      if (!v.records.length)
        this.records.append(
          h('div', { class: 'chat-empty', text: 'No records yet. Somebody go ride a bull.' }),
        );
    }

    if (v.chat.length !== this.lastChatLength) {
      this.lastChatLength = v.chat.length;
      const log = this.log;
      clear(log);
      if (!v.chat.length) log.append(h('div', { class: 'chat-empty', text: 'Quiet out here. Say howdy.' }));
      for (const line of v.chat)
        log.append(
          h(
            'div',
            { class: `chat-line${line.self ? ' self' : ''}` },
            h('strong', { text: line.name }),
            ` ${line.text}`,
          ),
        );
      log.scrollTop = log.scrollHeight;
    }
  }

  private person(p: OnlinePerson, where: string): HTMLElement {
    return h(
      'div',
      { class: 'person' },
      p.portrait
        ? h('img', { class: 'avatar', attrs: { src: p.portrait, alt: '' } })
        : h('div', { class: 'avatar', text: ':cowboy:' }),
      h(
        'div',
        { class: 'person-info' },
        h('div', { class: 'person-name', text: p.name }),
        p.detail ? h('div', { class: 'person-detail', text: p.detail }) : null,
      ),
      h('div', { class: 'person-where', text: where }),
    );
  }

  private privacyNote(): HTMLElement {
    return h('p', {
      class: 'online-note',
      text: "Players in a park connect directly to each other, so they can see each other's IP address, like most online games. Only share codes with folks you know. Everybody keeps their own money, rent and clock.",
    });
  }
}
